import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from './Icon';
import { faCartPlus, faCalendarCheck, faBolt } from '@fortawesome/free-solid-svg-icons';
import { useAddToCart, useSettings } from '../lib/store';
import { useI18n } from '../lib/i18n';
import { responsive } from '../lib/img';

export function ProductBadges({ p, className = 'absolute top-3 start-3' }) {
  const { t } = useI18n();
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {p.discountPercent > 0 && <span className="badge badge-sale">-{p.discountPercent}%</span>}
      {p.currentType && <span className={`badge ${p.currentType === 'AC' ? 'badge-ac' : 'badge-dc'}`}><FontAwesomeIcon icon={faBolt} />{p.currentType}</span>}
      {!p.inStock && <span className="badge badge-dark">{t('Out of stock')}</span>}
    </div>
  );
}

export default function ProductCard({ p }) {
  const { money } = useSettings();
  const addToCart = useAddToCart();
  const { t, pick } = useI18n();
  const meta = [p.powerKw && `${p.powerKw} kW`, p.connector].filter(Boolean);
  if (!meta.length && p.usage.length) meta.push(p.usage.map((u) => t(u === 'private' ? 'Residential' : 'Commercial')).join(' / '));
  const isService = p.category === 'installation';

  return (
    <article className="group @container flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition duration-200 hover:-translate-y-0.5 hover:shadow-card">
      <Link to={`/product/${p.slug}`} className="relative block aspect-square overflow-hidden bg-soft">
        <img {...responsive(p.images[0] || '/uploads/cable.svg', '(min-width: 1280px) 20vw, (min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw', 640)}
          alt={pick(p, 'name')} loading="lazy" decoding="async" width="400" height="400"
          className="size-full object-cover transition duration-300 group-hover:scale-105" />
        <ProductBadges p={p} />
      </Link>
      <div className="flex flex-1 flex-col gap-2 p-3 @[14rem]:p-4">
        <div className="flex flex-wrap gap-x-2 text-xs text-slate-500">
          {meta.map((m, i) => <span key={m} className={i ? "before:me-2 before:text-slate-300 before:content-['•']" : ''}>{m}</span>)}
        </div>
        <h3 className="text-sm leading-snug font-bold [overflow-wrap:anywhere] @[14rem]:text-[15px]">
          <Link to={`/product/${p.slug}`} className="hover:text-brand-600"><bdi>{pick(p, 'name')}</bdi></Link>
        </h3>
        <div className="mt-auto flex flex-wrap items-baseline gap-x-2">
          <span className="text-lg font-extrabold">{money(p.salePrice)}</span>
          {p.discountPercent > 0 && <span className="text-[13px] text-slate-500 line-through">{money(p.price)}</span>}
        </div>
        <button className="btn btn-primary btn-sm w-full whitespace-normal" disabled={!p.inStock} onClick={() => addToCart(p)}>
          <FontAwesomeIcon icon={isService ? faCalendarCheck : faCartPlus} />
          {p.inStock ? (isService ? t('Book service') : t('Add to cart')) : t('Out of stock')}
        </button>
      </div>
    </article>
  );
}

export function ProductGrid({ products, loading, cols = 4, skeletons = 4, empty = null }) {
  const grid = cols === 3
    ? 'grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3'
    : 'grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 xl:grid-cols-4';
  if (loading) return <div className={grid}>{Array.from({ length: skeletons }, (_, i) => <div key={i} className="skeleton aspect-[3/4]" />)}</div>;
  if (!products.length) return empty;
  return <div className={grid}>{products.map((p) => <ProductCard key={p.id} p={p} />)}</div>;
}
